import React, { useState, useCallback, useEffect } from 'react';
import { View, Heading, colors, Link } from '@fairygodboss/ui';
import window from 'global/window';
import moment from 'moment-timezone';
import { GridColumnWrapper } from './GridColumnWrapper';
import { useEventRegistration } from '../EventRegistrationContext';
import { CalendarEvent } from './CalendarEvent';
import { formatSponsorNames } from '../../../../pages/HomePage_v2/sections/Events';
import { getMonthByNumber } from '../../../Utilities/TimeFunctions';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';

export const CalendarEvents = () => {
    const { upcomingEvents, event } = useEventRegistration();
    const currentEventHash = event?.hash;
    const [eventLength, setEventLength] = useState(2);
    const [flexGap, setFlexGap] = useState(24);
    const [concatMonth, setConcatMonth] = useState(true);

    const handleResize = useCallback(() => {
        const isXs = window.innerWidth < 600;
        if (isXs) {
            setEventLength(3);
        } else {
            setEventLength(2);
        }

        const isSm = window.innerWidth < 905;
        if (isSm) {
            setFlexGap(16);
            setConcatMonth(false);
        } else {
            setFlexGap(24);
            setConcatMonth(true);
        }
    }, []);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const onClickMetric = (e) => {
        const sponsors = e.sponsors.map((s) => s.companyId).filter((c) => !!c);
        EmitMetric({
            event_id: e?.id,
            company_id: sponsors,
            misc_event_type: 'event-view-event-click',
            misc_event_count: 1,
        });
    };

    return (
        <View
            css={{
                '*': {
                    WebkitTapHighlightColor: 'transparent',
                },
            }}
        >
            <GridColumnWrapper
                backgroundColor="#F0F2F5"
                gridAlignment="center"
                px={{ _: '0px', sm: '16px', md: '24px', lg: '0px' }}
                gridColumnSpacing={{ xs: 12, sm: 12, md: 10, lg: 10 }}
            >
                <Heading
                    px={{ _: '16px', sm: '0px' }}
                    pt={{ _: '32px', md: '56px', lg: '48px' }}
                    pb={{ _: '32px', md: '40px' }}
                    fontWeight="700"
                    as="h2"
                    variant={{
                        _: 'headingSm',
                        sm: 'headingMd',
                        md: 'headingLg',
                    }}
                >
                    Browse upcoming events
                </Heading>
                <View
                    display="flex"
                    flexDirection="row"
                    overflow="inherit"
                    pb={{ xs: '56px', sm: '0px' }}
                    overflowX={{ _: 'scroll', sm: 'inherit' }}
                    px={{ _: '16px', sm: '0px' }}
                    css={{
                        gap: `${flexGap}px`,
                    }}
                >
                    {upcomingEvents
                        .filter((e) => e.hash !== currentEventHash)
                        .slice(0, eventLength)
                        .map((e) => {
                            const startDateUTC = new Date(
                                moment.tz(e?.startTime, 'UTC')
                            );
                            const momentStartTime = moment(startDateUTC).tz(
                                'America/New_York'
                            );
                            const momentEndTime = moment(
                                new Date(moment.tz(e?.endTime, 'UTC'))
                            ).tz('America/New_York');
                            const startTimeFormatted = momentStartTime.format(
                                'h:mm A'
                            );
                            const endTimeFormatted = momentEndTime.format(
                                'h:mm A'
                            );

                            const timeZone = 'ET';
                            const month = concatMonth
                                ? getMonthByNumber(momentStartTime.format('M'))
                                : momentStartTime.format('MMMM');

                            const day = momentStartTime.format('D');

                            const _sponsor = formatSponsorNames(e?.sponsors);

                            return (
                                <View
                                    display={{ _: 'flex', sm: 'contents' }}
                                    key={`calendar-event-${e.viewUrl}`}
                                >
                                    <CalendarEvent
                                        month={month}
                                        day={day}
                                        time={`${startTimeFormatted}-${endTimeFormatted} ${timeZone}`}
                                        title={e.eventType}
                                        sponsor={`${e?.label || ''} ${
                                            _sponsor || ''
                                        }`}
                                        body={e.title}
                                        url={e.viewUrl}
                                        onClick={() => onClickMetric(e)}
                                    />
                                </View>
                            );
                        })}
                </View>
                <View
                    css={{
                        'a:visited': {
                            color: `${colors.primary[60]} !important`,
                            'div > svg': {
                                path: {
                                    fill: colors.primary[60],
                                },
                            },
                            'a:hover': {
                                'div > svg': {
                                    path: {
                                        fill: colors.primary[80],
                                    },
                                },
                            },
                        },
                        a: {
                            textDecoration: 'none',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: '600',
                        },
                    }}
                >
                    <View
                        display={{ _: 'none', sm: '-webkit-box' }}
                        justifyContent="end"
                        pt={{ sm: '32px', md: '40px' }}
                        pb={{ sm: '40px', md: '56px', lg: '48px' }}
                        pr={{ _: '0px', sm: '8px', md: '12px' }}
                    >
                        <Link
                            href="/events"
                            target="_self"
                            icon
                            variant="standalone"
                        >
                            See more
                        </Link>
                    </View>
                </View>
            </GridColumnWrapper>
        </View>
    );
};

CalendarEvents.displayName = 'CalendarEvents';
